import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

/* palette — 与全站一致 */
const GOLD = '#B8892A';
const INK = '#1A1410';
const INK3 = '#6B5840';
const INK4 = '#A89070';
const BG = '#FAF8F5';
const BG2 = '#F4F1EB';
const BD2 = '#EAE5DC';

/* localStorage 键 */
const LS_HISTORY = 'yc_search_history';        // 搜索历史（全部词）
const LS_BRAND = 'yc_search_brand_history';    // 搜索品牌历史（命中品牌的词）
const MAX_HISTORY = 20;

function loadList(key) {
  try { const v = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(v) ? v : []; }
  catch { return []; }
}
function saveList(key, list) {
  try { localStorage.setItem(key, JSON.stringify(list.slice(0, MAX_HISTORY))); } catch {}
}
/** 把词推到列表最前、去重 */
function pushHistory(key, term) {
  const t = String(term || '').trim();
  if (!t) return;
  const list = loadList(key).filter(x => x !== t);
  list.unshift(t);
  saveList(key, list);
}

export default function Search() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [history, setHistory] = useState(() => loadList(LS_HISTORY));
  const [brandHistory, setBrandHistory] = useState(() => loadList(LS_BRAND));
  const [hotBrands, setHotBrands] = useState([]);   // { brand, count }[]
  const [brandSet, setBrandSet] = useState(new Set()); // 全部品牌(小写)，用于判断搜索词是否命中品牌

  // 拉热门品牌（当搜索页的「热门搜索」+ 判断品牌命中用）
  useEffect(() => {
    api.get('/lots/brands', { params: { limit: 60 } })
      .then(({ data }) => {
        const list = data?.data || data || [];
        setHotBrands(list.slice(0, 12));
        setBrandSet(new Set(list.map(b => String(b.brand || b).toLowerCase())));
      })
      .catch(() => {});
  }, []);

  const go = useCallback((term) => {
    const t = String(term || '').trim();
    if (!t) return;
    // 记历史
    pushHistory(LS_HISTORY, t);
    if (brandSet.has(t.toLowerCase())) pushHistory(LS_BRAND, t);
    // 跳结果页（复用 Browse）
    navigate(`/browse?keyword=${encodeURIComponent(t)}`);
  }, [navigate, brandSet]);

  const clearHistory = (key, setter) => { saveList(key, []); setter([]); };

  const Chip = ({ label, onClick, muted }) => (
    <span onClick={onClick} style={{
      display: 'inline-block', padding: '7px 14px', borderRadius: 18,
      background: '#fff', border: `1px solid ${BD2}`, color: muted ? INK4 : INK3,
      fontSize: 13, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
      transition: 'all .12s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = BD2; e.currentTarget.style.color = muted ? INK4 : INK3; }}
    >{label}</span>
  );

  const Section = ({ title, items, onClear, render }) => (
    !items.length ? null : (
      <div style={{ marginTop: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>{title}</span>
          {onClear && (
            <span onClick={onClear} style={{ marginLeft: 'auto', fontSize: 12, color: INK4, cursor: 'pointer' }}>清除历史</span>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>{items.map(render)}</div>
      </div>
    )
  );

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 60 }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px 0' }}>
        {/* 搜索框 */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8,
            background: BG2, border: `1px solid ${BD2}`, borderRadius: 22, padding: '9px 16px',
          }}>
            <span style={{ color: INK4, fontSize: 16 }}>🔍</span>
            <input
              autoFocus
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') go(input); }}
              placeholder="搜品牌 / 出品人 / 关键词（中日拼音都行）"
              enterKeyHint="search"
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 15, color: INK }}
            />
            {input && (
              <span onClick={() => setInput('')} style={{ color: INK4, cursor: 'pointer', fontSize: 14 }}>✕</span>
            )}
          </div>
          <button onClick={() => go(input)} style={{
            padding: '10px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
            background: GOLD, color: '#fff', fontSize: 14, fontWeight: 600,
          }}>搜索</button>
        </div>

        {/* 搜索品牌历史 */}
        <Section
          title="搜索品牌历史"
          items={brandHistory}
          onClear={() => clearHistory(LS_BRAND, setBrandHistory)}
          render={(t, i) => <Chip key={`b-${i}`} label={t} onClick={() => go(t)} />}
        />

        {/* 搜索历史 */}
        <Section
          title="搜索历史"
          items={history}
          onClear={() => clearHistory(LS_HISTORY, setHistory)}
          render={(t, i) => <Chip key={`h-${i}`} label={t} onClick={() => go(t)} />}
        />

        {/* 热门搜索（热门品牌） */}
        <Section
          title="热门搜索"
          items={hotBrands}
          render={(b, i) => <Chip key={`hot-${i}`} muted label={b.brand || b} onClick={() => go(b.brand || b)} />}
        />
      </div>
    </div>
  );
}
